import { supabase } from '../../lib/supabase';
import { Contribution } from '../../types/crowdfunding';
import { processPayment } from '../../services/paymentService';

interface PaymentOptions {
  paymentMethod: string;
  phoneNumber?: string;
}

/**
 * Récupère le titre de la campagne à partir de son ID
 */
async function getCampaignTitle(campaignId: string): Promise<string> {
  try {
    const { data, error } = await supabase
      .from('crowdfunding_campaigns')
      .select('title')
      .eq('id', campaignId)
      .single();
      
    if (error || !data) {
      return 'de financement';
    }
    
    return data.title;
  } catch (error) {
    console.error('Erreur lors de la récupération du titre de la campagne:', error);
    return 'de financement';
  }
}

/**
 * Récupère l'ID du contestant générique pour les paiements de crowdfunding
 * Utilise l'ID du contestant générique : 00000000-0000-0000-0000-000000000000
 */
async function getExistingContestant(): Promise<string> {
  try {
    // Vérifier d'abord si le contestant générique existe
    const { data, error } = await supabase
      .from('contestants')
      .select('id')
      .eq('id', '00000000-0000-0000-0000-000000000000')
      .single();
      
    if (error || !data) {
      // Si le contestant générique n'existe pas, le créer
      const { data: newContestant, error: createError } = await supabase
        .from('contestants')
        .upsert({
          id: '00000000-0000-0000-0000-000000000000',
          name: 'Contestant Générique',
          description: 'Compte générique pour les transactions sans candidat spécifique',
          status: 'active',
          contest_id: '00000000-0000-0000-0000-000000000000',
          user_id: '00000000-0000-0000-0000-000000000000',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .select()
        .single();

      if (createError) {
        console.error('Erreur lors de la création du contestant générique:', createError);
        throw new Error('Impossible de créer le contestant générique');
      }
      
      return newContestant.id;
    }
    
    return data.id;
  } catch (error) {
    console.error('Erreur lors de la récupération du contestant générique:', error);
    // En cas d'erreur, retourner l'ID du contestant générique de toute façon
    return '00000000-0000-0000-0000-000000000000';
  }
}

/**
 * Initialise un paiement pour une contribution en utilisant le système de paiement existant
 */
export const initiateContributionPayment = async (contribution: Contribution, options: PaymentOptions) => {
  try {
    // Récupérer le titre de la campagne
    const campaignTitle = await getCampaignTitle(contribution.campaign_id);
    
    // Récupérer un ID de contestant valide
    const contestantId = await getExistingContestant();
    
    // Préparer les données de paiement pour le système existant
    const paymentData = {
      amount: contribution.amount,
      votes_count: 1, // Valeur non nulle pour éviter les erreurs
      paymentMethod: options.paymentMethod,
      phoneNumber: options.phoneNumber,
      contestantId: contestantId, // Utiliser un ID de contestant valide
      customerName: contribution.contributor_name || 'Contributeur',
      customerEmail: contribution.contributor_email || `client-${Date.now()}@example.com`,
      description: `Contribution pour la campagne ${campaignTitle}`
    };

    // Utiliser le système de paiement existant
    const response = await processPayment(paymentData);
    
    // La référence de paiement est stockée dans la table transactions
    // Nous n'avons pas besoin de la stocker dans la table contributions
    // car elle ne contient pas de colonne payment_reference
    
    return response;
  } catch (error) {
    console.error('Erreur lors de l\'initialisation du paiement:', error);
    throw new Error(`Erreur lors de l'initialisation du paiement: ${error instanceof Error ? error.message : 'Erreur inconnue'}`);
  }
};
